import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { qualityApi } from '../../api/quality';

// 模拟质检数据
const MOCK_QUALITY_DATA = [
  {
    id: 1,
    orderNumber: 'SO-20230615-001',
    customerName: '张三',
    productName: '石佛寺文化纪念品',
    quantity: 2,
    qualityStatus: 'PENDING_RECEIPT',
    inspectionResult: null,
    createTime: '2023-06-15 10:30:00',
    updateTime: '2023-06-15 10:30:00'
  },
  {
    id: 2,
    orderNumber: 'SO-20230620-002',
    customerName: '李四',
    productName: '佛教工艺画',
    quantity: 1,
    qualityStatus: 'IN_INSPECTION',
    inspectionResult: null,
    createTime: '2023-06-20 14:45:00',
    updateTime: '2023-06-20 14:45:00'
  },
  {
    id: 3,
    orderNumber: 'SO-20230701-003',
    customerName: '王五',
    productName: '佛珠手链',
    quantity: 3,
    qualityStatus: 'QUALITY_FAILED',
    inspectionResult: '材质不符合标准',
    createTime: '2023-07-01 09:15:00',
    updateTime: '2023-07-01 09:15:00'
  }
];

// 获取质检列表
export const fetchQualityList = createAsyncThunk(
  'quality/fetchList',
  async ({ page = 1, pageSize = 10, ...params }, { rejectWithValue }) => {
    try {
      const response = await qualityApi.getQualityList({ page, pageSize, ...params });
      return response.data;
    } catch (error) {
      console.warn('获取质检列表失败，使用模拟数据', error);
      
      // 模拟搜索功能
      const filteredData = MOCK_QUALITY_DATA.filter(qualityItem => {
        const orderNumberMatch = !params.orderNumber || 
          qualityItem.orderNumber.toLowerCase().includes(params.orderNumber.toLowerCase());
        
        const customerNameMatch = !params.customerName || 
          qualityItem.customerName.toLowerCase().includes(params.customerName.toLowerCase());
        
        const qualityStatusMatch = !params.qualityStatus || 
          qualityItem.qualityStatus === params.qualityStatus;
        
        const timeMatch = !params.startTime || !params.endTime || 
          (qualityItem.createTime >= params.startTime && qualityItem.createTime <= params.endTime);
        
        return orderNumberMatch && customerNameMatch && 
               qualityStatusMatch && timeMatch;
      });

      // 模拟分页
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      
      return {
        list: paginatedData,
        total: filteredData.length,
        page,
        pageSize
      };
    }
  }
);

// 确认收货
export const confirmReceipt = createAsyncThunk(
  'quality/confirmReceipt',
  async (id, { rejectWithValue }) => {
    try {
      const response = await qualityApi.confirmReceipt(id);
      return response.data;
    } catch (error) {
      console.warn('确认收货失败，使用模拟数据', error);
      const index = MOCK_QUALITY_DATA.findIndex(q => q.id === id);
      if (index !== -1) {
        const updatedQualityItem = { 
          ...MOCK_QUALITY_DATA[index],
          qualityStatus: 'PENDING_RECEIPT',
          updateTime: new Date().toLocaleString()
        };
        MOCK_QUALITY_DATA[index] = updatedQualityItem;
        return updatedQualityItem;
      }
      return rejectWithValue('质检项目不存在');
    }
  }
);

// 送检
export const sendForInspection = createAsyncThunk(
  'quality/sendForInspection',
  async (id, { rejectWithValue }) => {
    try {
      const response = await qualityApi.sendForInspection(id);
      return response.data;
    } catch (error) {
      console.warn('送检失败，使用模拟数据', error);
      const index = MOCK_QUALITY_DATA.findIndex(q => q.id === id);
      if (index !== -1) {
        const updatedQualityItem = { 
          ...MOCK_QUALITY_DATA[index],
          qualityStatus: 'IN_INSPECTION',
          updateTime: new Date().toLocaleString()
        };
        MOCK_QUALITY_DATA[index] = updatedQualityItem;
        return updatedQualityItem;
      }
      return rejectWithValue('质检项目不存在');
    }
  }
);

// 质检不合格
export const rejectQualityCheck = createAsyncThunk(
  'quality/rejectQualityCheck',
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const response = await qualityApi.rejectQualityCheck(id, data);
      return response.data;
    } catch (error) {
      console.warn('质检不合格处理失败，使用模拟数据', error);
      const index = MOCK_QUALITY_DATA.findIndex(q => q.id === id);
      if (index !== -1) {
        const updatedQualityItem = { 
          ...MOCK_QUALITY_DATA[index],
          qualityStatus: 'QUALITY_FAILED',
          inspectionResult: data.inspectionResult || '未提供具体原因',
          updateTime: new Date().toLocaleString()
        };
        MOCK_QUALITY_DATA[index] = updatedQualityItem;
        return updatedQualityItem;
      }
      return rejectWithValue('质检项目不存在');
    }
  }
);

const qualitySlice = createSlice({
  name: 'quality',
  initialState: {
    qualityList: [],
    qualityPagination: {
      current: 1,
      pageSize: 10,
      total: 0
    },
    loading: false,
    error: null
  },
  reducers: {
    setQualityPagination: (state, action) => {
      state.qualityPagination = { ...state.qualityPagination, ...action.payload };
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取质检列表
      .addCase(fetchQualityList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQualityList.fulfilled, (state, action) => {
        state.loading = false;
        state.qualityList = action.payload.list;
        state.qualityPagination = {
          current: action.payload.page,
          pageSize: action.payload.pageSize,
          total: action.payload.total
        };
      })
      .addCase(fetchQualityList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.qualityList = MOCK_QUALITY_DATA;
        state.qualityPagination.total = MOCK_QUALITY_DATA.length;
      })
      // 确认收货
      .addCase(confirmReceipt.fulfilled, (state, action) => {
        const index = state.qualityList.findIndex(q => q.id === action.payload.id);
        if (index !== -1) {
          state.qualityList[index] = action.payload;
        }
      })
      // 送检
      .addCase(sendForInspection.fulfilled, (state, action) => {
        const index = state.qualityList.findIndex(q => q.id === action.payload.id);
        if (index !== -1) {
          state.qualityList[index] = action.payload;
        }
      })
      // 质检不合格
      .addCase(rejectQualityCheck.fulfilled, (state, action) => {
        const index = state.qualityList.findIndex(q => q.id === action.payload.id);
        if (index !== -1) {
          state.qualityList[index] = action.payload;
        }
      });
  }
});

export const { setQualityPagination } = qualitySlice.actions;
export default qualitySlice.reducer; 